import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, UserPlus, MessageSquare, Heart, MessageCircle, Reply, Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNotifications, type Notification } from "@/hooks/useNotifications";

const ICONS: Record<string, typeof Bell> = {
  follow: UserPlus,
  message: MessageSquare,
  list_like: Heart,
  list_comment: MessageCircle,
  discussion_reply: Reply,
};

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

function Row({ n, onRead, onRemove }: { n: Notification; onRead: (id: string) => void; onRemove: (id: string) => void }) {
  const Icon = ICONS[n.type] ?? Bell;
  const navigate = useNavigate();
  const handleClick = () => {
    if (!n.read_at) onRead(n.id);
    if (n.link) navigate(n.link);
  };
  return (
    <div
      className={cn(
        "group relative flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors cursor-pointer hover:bg-muted/60",
        !n.read_at && "bg-primary/5"
      )}
      onClick={handleClick}
    >
      <div className={cn(
        "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
        n.read_at ? "bg-muted text-muted-foreground" : "bg-primary/15 text-primary"
      )}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn("text-sm leading-snug", !n.read_at && "font-medium text-foreground")}>{n.title}</p>
        {n.body && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</p>}
        <p className="text-[10px] text-muted-foreground/70 mt-1 uppercase tracking-wide">{timeAgo(n.created_at)}</p>
      </div>
      <button
        aria-label="Remove notification"
        onClick={(e) => { e.stopPropagation(); onRemove(n.id); }}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function NotificationsBell() {
  const { items, unread, loading, markAllRead, markRead, remove } = useNotifications(30);
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={unread ? `${unread} unread notifications` : "Notifications"}
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/80 hover:text-foreground hover:bg-muted/60 transition"
        >
          <Bell className="h-[18px] w-[18px]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-4 text-center ring-2 ring-background">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2.5 border-b">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead}>
              <Check className="h-3.5 w-3.5 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[440px]">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-7 w-7 mx-auto mb-2 text-muted-foreground/60" />
              <p className="text-sm font-medium">You're all caught up</p>
              <p className="text-xs text-muted-foreground mt-1">Follows, messages, and replies show up here.</p>
            </div>
          ) : (
            <div className="p-1.5 space-y-0.5">
              {items.map((n) => (
                <Row key={n.id} n={n} onRead={markRead} onRemove={remove} />
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="border-t px-3 py-2 text-center">
          <Link to="/notifications" onClick={() => setOpen(false)} className="text-xs text-muted-foreground hover:text-foreground">
            View all notifications
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
